/**
 * apps/web/src/components/Layout.tsx
 *
 * Mobile-first app shell.
 *
 *   - Desktop (>= md): a fixed left sidebar with the full nav + a user footer.
 *   - Mobile (< md):   the content fills the screen and a bottom tab bar shows
 *                      the 4 primary sections plus an "Altro" button that opens
 *                      a slide-up drawer with the remaining sections.
 *
 * Content is rendered through <Outlet/>; route screens supply their own page
 * header. The shell is purely chrome.
 */

import React, { useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { QueryErrorResetBoundary } from '@tanstack/react-query';
import { LogOut, Menu, X } from 'lucide-react';
import { useNavItems, usePrimaryNavItems } from './nav-items';
import { Wordmark } from './Wordmark';
import { ThemeToggle } from './ThemeToggle';
import { Avatar } from './ui/Avatar';
import { ErrorBoundary } from './ErrorBoundary';
import { useAuth } from '../auth/AuthContext';
import { useStrings } from '../i18n';
import { cn } from '../lib/cn';

function navLinkClass(isActive: boolean): string {
  return cn(
    'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-150 ease-out',
    isActive
      ? 'bg-accent-soft font-semibold text-accent-soft-foreground shadow-[inset_3px_0_0_var(--accent)]'
      : 'text-muted hover:bg-surface-hover hover:text-foreground',
  );
}

export function Layout(): React.ReactElement {
  const { user, workspace, logout } = useAuth();
  const strings = useStrings();
  const navItems = useNavItems();
  const primaryNavItems = usePrimaryNavItems();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const fullName = user ? `${user.firstName} ${user.lastName}`.trim() : '';

  // Close the "Altro" drawer whenever the route changes. The pathname dep is the
  // trigger (the body only resets state), so Biome's exhaustive-deps check is
  // intentionally overridden here.
  // biome-ignore lint/correctness/useExhaustiveDependencies: pathname is the intended trigger
  React.useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // Escape closes the "Altro" drawer. It is an `aria-modal` dialog, so a
  // keyboard user must be able to dismiss it without a pointer - same contract
  // as SlideOver and RouteDrawer.
  React.useEffect(() => {
    if (!drawerOpen) return;
    function onKey(event: KeyboardEvent): void {
      if (event.key === 'Escape') setDrawerOpen(false);
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [drawerOpen]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-60 flex-col border-r border-border bg-surface px-3 py-5 md:flex">
        <div className="px-2 pb-6">
          <Wordmark />
          {workspace ? (
            <p className="mt-1 truncate px-0.5 text-xs text-muted">{workspace.name}</p>
          ) : null}
        </div>

        <nav className="flex flex-1 flex-col gap-1" aria-label={strings.nav.ariaPrimary}>
          {navItems.map((item) => (
            <NavLink key={item.key} to={item.to} className={({ isActive }) => navLinkClass(isActive)}>
              <item.icon className="h-[18px] w-[18px] shrink-0" aria-hidden="true" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="mt-3 border-t border-border pt-3">
          <div className="mb-2 flex justify-center">
            <ThemeToggle />
          </div>
          <div className="flex items-center gap-3 px-2 py-2">
            <Avatar name={fullName || 'glarno'} src={user?.avatarUrl} size="sm" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-foreground">{fullName}</p>
              {user ? <p className="truncate text-xs text-muted">{user.email}</p> : null}
            </div>
          </div>
          <button
            type="button"
            onClick={() => void logout()}
            className="mt-1 flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-muted transition-colors hover:bg-surface-hover hover:text-accent"
          >
            <LogOut className="h-[18px] w-[18px]" aria-hidden="true" />
            {strings.nav.logout}
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="md:pl-60">
        <main className="mx-auto w-full max-w-5xl px-4 pb-24 pt-6 md:px-8 md:pb-10 md:pt-8">
          {/* Per-route boundary: a crash in one page keeps the shell + nav alive.
              Keyed on pathname so navigating away clears it; onReset bridges React
              Query's reset so "Riprova" also refetches the queries that threw. */}
          <QueryErrorResetBoundary>
            {({ reset }) => (
              <ErrorBoundary boundaryName="route" onReset={reset} resetKeys={[location.pathname]}>
                <Outlet />
              </ErrorBoundary>
            )}
          </QueryErrorResetBoundary>
        </main>
      </div>

      {/* Mobile bottom tab bar */}
      <nav
        className="fixed inset-x-0 bottom-0 z-30 flex border-t border-border bg-surface/95 pb-safe backdrop-blur md:hidden"
        aria-label={strings.nav.ariaQuick}
      >
        {primaryNavItems.map((item) => (
          <NavLink
            key={item.key}
            to={item.to}
            className={({ isActive }) =>
              cn(
                'flex flex-1 flex-col items-center gap-1 py-2.5 text-[0.65rem] font-medium',
                isActive ? 'text-accent' : 'text-muted',
              )
            }
          >
            <item.icon className="h-5 w-5" aria-hidden="true" />
            {item.label}
          </NavLink>
        ))}
        <button
          type="button"
          onClick={() => setDrawerOpen(true)}
          className="flex flex-1 flex-col items-center gap-1 py-2.5 text-[0.65rem] font-medium text-muted"
          aria-haspopup="dialog"
          aria-expanded={drawerOpen}
        >
          <Menu className="h-5 w-5" aria-hidden="true" />
          {strings.nav.altro}
        </button>
      </nav>

      {/* Mobile "Altro" drawer */}
      {drawerOpen ? (
        <div
          className="fixed inset-0 z-40 md:hidden"
          // biome-ignore lint/a11y/useSemanticElements: native <dialog> only becomes modal
          // via imperative showModal() and supplies its own ::backdrop, which conflicts
          // with this drawer's conditional render and md:hidden layering.
          role="dialog"
          aria-modal="true"
          aria-label={strings.nav.altro}
        >
          <div
            className="absolute inset-0 bg-backdrop backdrop-blur-sm"
            onMouseDown={() => setDrawerOpen(false)}
            aria-hidden="true"
          />
          <div className="absolute inset-x-0 bottom-0 rounded-t-2xl border-t border-border bg-surface p-4 pb-safe">
            <div className="mb-3 flex items-center justify-between px-1">
              <Wordmark size="md" />
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="rounded-lg p-2 text-muted hover:bg-surface-hover hover:text-foreground"
                aria-label={strings.common.cancel}
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
            <nav className="grid grid-cols-3 gap-2" aria-label={strings.nav.ariaAll}>
              {navItems.map((item) => (
                <NavLink
                  key={item.key}
                  to={item.to}
                  className={({ isActive }) =>
                    cn(
                      'flex flex-col items-center gap-2 rounded-xl border border-border px-2 py-4 text-center text-xs font-medium',
                      isActive ? 'border-accent bg-accent-soft text-accent-soft-foreground' : 'text-muted',
                    )
                  }
                >
                  <item.icon className="h-5 w-5" aria-hidden="true" />
                  {item.label}
                </NavLink>
              ))}
            </nav>
            <div className="mt-3 flex justify-center">
              <ThemeToggle />
            </div>
            <button
              type="button"
              onClick={() => void logout()}
              className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl border border-border px-3 py-3 text-sm font-medium text-accent"
            >
              <LogOut className="h-[18px] w-[18px]" aria-hidden="true" />
              {strings.nav.logout}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
